import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { Button, Group } from '@mantine/core';
import { undo } from '@tiptap/pm/history';
import { registerOverlayPortal } from '@puckeditor/core';
import { htmlToFormattedBlocks, pastePalette } from '../../shared/paste/htmlPaste';
import {
  isTextOnlyPasteShortcut, PASTE_CHOICE_OPTIONS, PASTE_KEPT, PASTE_TEXT_ONLY,
} from '../../shared/paste/pasteChoice';
import { usePuckRichTextStyle } from '../context/PuckRichTextStyleContext';
import { formatFontSizeRem, parseFontSizeRem } from '../utils/proposalRichTextFonts';
import { dispatchPaste, isEditorClipboard, pasteSlice } from './styledPaste';

// Les tailles du site sont en rem : la palette du collage compte en px.
const ROOT_PX = 16;
const toPx = (value) => {
  const rem = parseFontSizeRem(value, ROOT_PX);
  return rem == null ? null : rem * ROOT_PX;
};
const toFontSize = (px) => formatFontSizeRem(px / ROOT_PX);

const keepFocus = (event) => {
  event.preventDefault();
  event.stopPropagation();
};
const stopBubble = (event) => event.stopPropagation();

/* Pastille sous le texte. Ni le focus de l'éditeur ni la sélection du bloc ne
   doivent bouger à son clic. Puck sélectionne et désélectionne par des
   écouteurs natifs posés avant la racine React (cf. usePuckEditableSurface) :
   tout passe donc par des écouteurs natifs, `click` compris, qui arrêtent
   l'événement avant eux. */
const PasteChoice = ({ value, onChange }) => {
  const ref = useRef(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    const element = ref.current;
    if (!element) return undefined;
    const choose = (event) => {
      event.stopPropagation();
      const choice = event.target.closest('[data-paste-choice]')?.dataset.pasteChoice;
      if (choice) onChangeRef.current(choice);
    };
    ['pointerdown', 'mousedown'].forEach((type) => element.addEventListener(type, keepFocus));
    ['pointerup', 'mouseup'].forEach((type) => element.addEventListener(type, stopBubble));
    element.addEventListener('click', choose);
    const cleanupPortal = registerOverlayPortal(element);
    return () => {
      ['pointerdown', 'mousedown'].forEach((type) => element.removeEventListener(type, keepFocus));
      ['pointerup', 'mouseup'].forEach((type) => element.removeEventListener(type, stopBubble));
      element.removeEventListener('click', choose);
      cleanupPortal?.();
    };
  }, []);

  return (
    <Group ref={ref} gap={4} justify="flex-end" style={{ marginTop: 4 }}>
      {PASTE_CHOICE_OPTIONS.map((option) => (
        <Button
          key={option.value}
          size="compact-xs"
          variant={option.value === value ? 'filled' : 'subtle'}
          data-paste-choice={option.value}
        >
          {option.label}
        </Button>
      ))}
    </Group>
  );
};

/**
 * Collage riche (Word, Google Docs, web) d'un texte TipTap du site, comme
 * `useStyledPaste` du classique : formats ramenés à la palette, aux tailles et
 * à la couleur du texte du thème ; Mod + Maj + V colle le texte seul ; après un
 * collage mis en forme, une pastille propose l'autre version.
 *
 * Rend des gestionnaires stables, à brancher sur `useEditor` (ses options ne
 * sont lues qu'à la création) : `handlePaste`, `handleKeyDown`,
 * `handleKeyUp` (editorProps), `onEditorUpdate` (onUpdate), `dismiss` (fin
 * d'édition) ; et `pasteChoice`, élément à rendre sous l'éditeur, dans son
 * bloc.
 */
export function useStyledPaste({ textStyle }) {
  const { swatches, fontSizes } = usePuckRichTextStyle();
  const paletteOptions = useMemo(() => {
    const colors = swatches.filter(({ value }) => value).map(({ value }) => ({ label: value, style: value }));
    return {
      colors,
      highlightColors: colors,
      defaultColor: textStyle?.color || null,
      fontSizes: fontSizes.map(({ value }) => toPx(value)).filter(Boolean),
      defaultFontSize: textStyle?.fontSize
        ? toPx(typeof textStyle.fontSize === 'number' ? `${textStyle.fontSize}px` : textStyle.fontSize)
        : null,
    };
  }, [swatches, fontSizes, textStyle]);
  const paletteOptionsRef = useRef(paletteOptions);
  paletteOptionsRef.current = paletteOptions;

  // Posé au keydown du raccourci, lu par le collage qui suit, effacé au keyup.
  const textOnlyPasteRef = useRef(false);
  // Dernier collage mis en forme : ses deux versions et le document qu'il a
  // laissé. Lu par les écouteurs de l'éditeur, d'où la ref en plus de l'état.
  const choiceRef = useRef(null);
  const [choice, setChoiceState] = useState(null);
  const setChoice = useCallback((next) => {
    choiceRef.current = next;
    setChoiceState(next);
  }, []);
  const choosingRef = useRef(false);

  const handlePaste = useCallback((view, event) => {
    const textOnly = textOnlyPasteRef.current;
    textOnlyPasteRef.current = false;
    setChoice(null);
    const html = event.clipboardData?.getData('text/html');
    if (isEditorClipboard(html)) return false;
    const blocks = htmlToFormattedBlocks(html);
    if (!blocks) return false;

    const options = { palette: pastePalette(paletteOptionsRef.current), toFontSize };
    const textOnlySlice = pasteSlice(view.state, blocks, { ...options, textOnly: true });
    if (textOnly) {
      dispatchPaste(view, textOnlySlice);
      return true;
    }
    const keptSlice = pasteSlice(view.state, blocks, { ...options, textOnly: false });
    dispatchPaste(view, keptSlice);
    if (!keptSlice.eq(textOnlySlice)) {
      setChoice({
        view,
        [PASTE_KEPT]: keptSlice,
        [PASTE_TEXT_ONLY]: textOnlySlice,
        value: PASTE_KEPT,
        doc: view.state.doc,
      });
    }
    return true;
  }, [setChoice]);

  // Rejoue le collage depuis l'état d'avant : une seule étape d'annulation.
  const choose = useCallback((value) => {
    const current = choiceRef.current;
    if (!current || current.value === value) return;
    const { view } = current;
    choosingRef.current = true;
    undo(view.state, view.dispatch);
    dispatchPaste(view, current[value]);
    choosingRef.current = false;
    setChoice({ ...current, value, doc: view.state.doc });
  }, [setChoice]);

  // Toute autre modification du texte, ou la fin de l'édition, retire la
  // pastille.
  const onEditorUpdate = useCallback((editor) => {
    if (choosingRef.current || !choiceRef.current) return;
    if (editor.state.doc !== choiceRef.current.doc) setChoice(null);
  }, [setChoice]);
  const dismiss = useCallback(() => {
    if (choiceRef.current) setChoice(null);
  }, [setChoice]);

  const handleKeyDown = useCallback((view, event) => {
    textOnlyPasteRef.current = isTextOnlyPasteShortcut(event);
    return false;
  }, []);
  const handleKeyUp = useCallback(() => {
    textOnlyPasteRef.current = false;
    return false;
  }, []);

  const pasteChoice = choice && <PasteChoice value={choice.value} onChange={choose} />;

  return {
    handlePaste, handleKeyDown, handleKeyUp, onEditorUpdate, dismiss, pasteChoice,
  };
}
